import React, { useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import {
  openBrief,
  openBriefProject,
  openBriefSubmission,
} from '@disco/state-management/actions/ExplorerActions'
import {
  getBriefProject,
  loadBriefProject,
} from '@disco/state-management/reducers/entities/briefProjects'
import { loadBrief } from '@disco/state-management/reducers/entities/briefs'

import { useDispatch, useSelector } from 'hooks/use-redux'

const BriefExplorerLoader = () => {
  const params =
    useParams<{ briefId: string; playlistId: string; projectId: string }>()
  const { briefId, playlistId, projectId } = params

  const dispatch = useDispatch()

  // Check if project already exists in Redux state (only if projectId exists)
  const existingProject = useSelector(state =>
    projectId
      ? getBriefProject(state, { briefProjectId: parseInt(projectId) })
      : null,
  )

  useEffect(() => {
    const handleNavigation = async () => {
      if (projectId) {
        // We're in a project context, need to load project first
        try {
          let project

          // Check if project already exists in Redux state
          if (existingProject) {
            // Use existing project from Redux state (skip API call)
            project = existingProject
          } else {
            // Load the project data from API
            project = await dispatch(
              loadBriefProject(parseInt(projectId)),
            ).unwrap()
          }

          // Load the project first to establish context (add type property for BriefProjectListApiItem)
          const projectWithType = { ...project, type: 'project' as const }
          await dispatch(openBriefProject(null, null, projectWithType, true))

          // Then handle brief or brief submission
          if (briefId && !playlistId) {
            // Open brief within project context (skip loading briefs since we're in project)
            const brief = await dispatch(loadBrief(parseInt(briefId))).unwrap()
            dispatch(openBrief(null, null, brief, true, true))
          } else if (briefId && playlistId) {
            // Open brief submission within project context (skip loading briefs since we're in project)
            dispatch(
              openBriefSubmission(
                parseInt(briefId),
                parseInt(playlistId),
                true,
                parseInt(projectId),
              ),
            )
          }
        } catch (error) {
          console.error('Failed to load brief project:', error)
          // Handle error - maybe show a notification or redirect
        }
      } else {
        // Standalone brief (no project context)
        if (briefId && !playlistId) {
          const brief = await dispatch(loadBrief(parseInt(briefId))).unwrap()
          dispatch(openBrief(null, null, brief, true))
        } else if (briefId && playlistId) {
          dispatch(openBriefSubmission(parseInt(briefId), parseInt(playlistId)))
        }
      }
    }

    handleNavigation()
  }, [projectId, briefId, playlistId, dispatch])

  return null
}

export const BriefExplorerPage = BriefExplorerLoader
